import { providers, Contract, Signer } from "ethers";
import UserWalletFactoryAbi from "./abi/UserWalletFactory";
import Wallet2WalletAbi from "./abi/Wallet2Wallet";
import WalletProxy from "./WalletProxy";

enum ContractRoles {
    TradingProtocol,
    WalletFactory,
    WalletProxy,
}

const VOID_ADDRESS = "0x0000000000000000000000000000000000000000"

const networkRolesMapping: any = {
    1: {
        [ContractRoles.TradingProtocol]: "0x2176750EAEE750C11b8163265eA7015909437d03",
        [ContractRoles.WalletFactory]: "0xdEA6ea4ee509368EEe917d4e7b5747fBbd4ED8bD",
    },
    137: {
        [ContractRoles.TradingProtocol]: "0xE61241e9dD8AA597Aab8a2091D1d8d9117e6024D",
        [ContractRoles.WalletFactory]: "0xe04b1B72350194774584C0843db09AF9a323a3d6",
    },
    5777: {
        [ContractRoles.TradingProtocol]: "0x957E0d699d01F312d840da196500CFDbc51B33Dc",
        [ContractRoles.WalletFactory]: "0x1FF5DE4ff273BD63b78CB8D5B69b09aD3BD0940a",
    },
    3: {
        [ContractRoles.TradingProtocol]: "0xE9Aad7440333Fd9CA0379Dd5C3142Aec768D5092",
        [ContractRoles.WalletFactory]: "0xd38C3D2c91B8d413ec311f1ee76EdB8F982af918",
    },
    56: {
        [ContractRoles.TradingProtocol]: "0x90EE4b10352823a8052C24A03FE6859bcBEbEF34",
        [ContractRoles.WalletFactory]: "0x22628AD51BBd4736323C868951c2e1c15e4a5D51",
    },
    250: {
        [ContractRoles.TradingProtocol]: "0xDc0794D004865330292D3Dc8aC1a676D1dAf8618",
        [ContractRoles.WalletFactory]: "0x814C5fC9968D34A1e1A9419dEc35596b2864912d",
    },
    43114: {
        [ContractRoles.TradingProtocol]: "0xEbb303A257035A4401724a392fe23B62a8f75c7f",
        [ContractRoles.WalletFactory]: "0xE276d0a73a78d9ceE99fc68C8Ca211410420942b",
    },
};
/**
 * Interface over contract ABIs
 */
class LimitOrdersProxy {
    network_id: number;
    provider: any;

    constructor(network_id: number, provider?: Signer) {
        this.network_id = network_id;
        this.provider = provider || this.createProvider(network_id);
    }

    private createProvider = (network_id: number) => {
        switch (network_id) {
            case 1:
                return new providers.JsonRpcProvider("https://mainnet.infura.io/v3/73cef697dd77451f90d1a45055d734de")
            case 3:
                return new providers.JsonRpcProvider("https://ropsten.infura.io/v3/c5f83ba6902e45fcbe6d518d5c516475")
            case 56:
                return new providers.JsonRpcProvider("https://bsc-dataseed1.binance.org")
            case 137:
                return new providers.JsonRpcProvider("https://polygon-rpc.com")
            case 250:
                return new providers.JsonRpcProvider("https://rpc.ftm.tools/")
            case 5777:
                return new providers.JsonRpcProvider("http://localhost:7545")
            case 43114:
                return new providers.JsonRpcProvider("https://rpc.ankr.com/avalanche")
            default:
                return undefined;
        }
    }

    public getProvider() {
        return this.provider;
    }

    public getAddress(role: ContractRoles): string | null {
        const network = networkRolesMapping[this.network_id];
        return network ? network[role] : null;
    }

    /**
     * Compare wallet proxy result with zero
     * @param wallet 
     * @returns 
     */
    public walletHasProxy = async (wallet: string): Promise<boolean | null> => {
        try {
            const value = await this.getWalletProxyAddress(wallet);
            if (value == VOID_ADDRESS || value == null) {
                return false;
            }
            return true;
        } catch (err) {
            console.error(err);
            return null;
        }
    }

    /**
     * Get wallet proxy address
     * @param wallet 
     * @returns 
     */
    public getWalletProxyAddress = async (wallet: string): Promise<string | null> => {
        try {
            const UserWalletFactory = this.getUserWalletFactoryContract();
            if (!UserWalletFactory) {
                return null;
            }
            const value = await UserWalletFactory.getUserWallet(wallet);
            return value;
        } catch (err) {
            console.error(err);
            return null;
        }
    }

    /**
     * Create wallet proxy instance
     * @param wallet 
     */
    public getWalletProxy = async (wallet: string, signer: Signer): Promise<WalletProxy> =>
        new WalletProxy(wallet, signer)

    public createWalletProxy = async (wallet: string, signer: Signer): Promise<any | boolean> => {
        try {
            const UserWalletFactory = this.getUserWalletFactoryContract(signer);
            if (!UserWalletFactory) {
                return false;
            }
            const target = this.getAddress(ContractRoles.TradingProtocol);
            if (!target) {
                return false;
            }
            
            const result = await UserWalletFactory.deployUserWallet(target, VOID_ADDRESS);
            return result;
        } catch (err) {
            console.error(err);
            return false;
        }
    }

    /**
     * Get trading protocol instance
     * @returns 
     */
    public getTradingProtocolContract() {
        const address = this.getAddress(ContractRoles.TradingProtocol);
        if (!address) {
            return null;
        }
        const contractInstance = new Contract(address, Wallet2WalletAbi, this.provider);
        return contractInstance;
    }

    /**
     * Get User Wallet Factory instance
     * @returns 
     */
    public getUserWalletFactoryContract(signer?: Signer) {
        const address = this.getAddress(ContractRoles.WalletFactory);
        if (!address) {
            return null;
        }
        const contractInstance = new Contract(address, UserWalletFactoryAbi, signer ? signer : this.provider);
        return contractInstance;
    }
}

export default LimitOrdersProxy;
